/**
 * An amount, grouped as it is typed.
 *
 * `3500000` is a figure somebody has to count with a finger. `3,500,000` is one they can read,
 * and misreading a rent by a factor of ten is a real error with a real cost.
 *
 * **Grouping never reaches the server.** The visible box shows `3,500,000`; a hidden field beside
 * it carries `3500000`, and that is what is submitted. `parseMoney` would strip the commas
 * anyway, but relying on that would make every future money endpoint depend on a detail nobody
 * would think to check. The rule here is simpler: separators are a display concern and stop at
 * the edge of the control.
 *
 * **The currency decides the decimals, and nothing else.** This product groups with a comma and
 * separates decimals with a dot for every currency it supports, so there is no locale in which
 * the two characters swap meaning. What does vary is whether a currency has a minor unit at all —
 * UGX, RWF, XOF and XAF do not — and their fields refuse a decimal point rather than accepting
 * one and rounding it away out of sight.
 *
 * **The caret stays where it was put.** Reformatting rewrites the whole string, which normally
 * throws the caret to the end and makes the field unusable for correcting a digit in the middle.
 * The position is remembered by how many digits precede it, which is stable across regrouping.
 */

import { useLayoutEffect, useRef, useState, type InputHTMLAttributes } from "react";
import { currencyInfo, formatAmountInput } from "@rentorng/core";
import { useSession } from "@/lib/session";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "defaultValue" | "onChange"> & {
  name?: string;
  /** Defaults to the agency's own currency — pass one only where the amount is in another. */
  currency?: string;
  /** The canonical amount, ungrouped — `3500000`, not `3,500,000`. */
  defaultValue?: string | number | null;
  /** Controlled use: the canonical amount, and a callback given the canonical amount. */
  value?: string;
  onChange?: (canonical: string) => void;
  /** Show the currency symbol inside the field. Off where a column of them would be noise. */
  symbol?: boolean;
};

/** Digits before an index — the one thing about a caret that survives regrouping. */
const digitsBefore = (text: string, index: number) => text.slice(0, index).replace(/\D/g, "").length;

/** The offset that puts the caret after `count` digits. */
function offsetAfterDigits(text: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (/\d/.test(text[i])) {
      seen += 1;
      if (seen === count) return i + 1;
    }
  }
  return text.length;
}

export function MoneyInput({
  name,
  currency,
  defaultValue,
  value,
  onChange,
  symbol = false,
  className = "",
  placeholder,
  ...rest
}: Props) {
  const { org } = useSession();
  // The agency's currency is the right answer almost everywhere, and making every call site
  // repeat it is how one of them ends up with a stale default nobody notices.
  const code = currency ?? String(org?.currency ?? "NGN");
  const controlled = value !== undefined;
  const info = currencyInfo(code);

  /**
   * The shape of the number, not an instruction.
   *
   * Every money field in the product gets the same one from here, which is the only way "some
   * boxes have a hint and some do not" stays fixed. It follows the currency: a field that refuses
   * a decimal point must not show one in its own placeholder.
   */
  const hint = placeholder ?? (info.decimals === 0 ? "0" : `0.${"0".repeat(info.decimals)}`);

  const seed = formatAmountInput(String(defaultValue ?? value ?? ""), code);
  const [display, setDisplay] = useState(seed.display);
  const [canonical, setCanonical] = useState(seed.canonical);

  const inputRef = useRef<HTMLInputElement>(null);
  /** Set on every edit, applied after React has painted the reformatted string. */
  const caret = useRef<number | null>(null);

  // A controlled caller can replace the value from outside; follow it without fighting the caret.
  const shown = controlled ? formatAmountInput(value, code).display : display;

  useLayoutEffect(() => {
    if (caret.current === null || !inputRef.current) return;
    const target = offsetAfterDigits(inputRef.current.value, caret.current);
    inputRef.current.setSelectionRange(target, target);
    caret.current = null;
  });

  const handle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const position = event.target.selectionStart ?? raw.length;
    caret.current = digitsBefore(raw, position);

    const next = formatAmountInput(raw, code);
    setDisplay(next.display);
    setCanonical(next.canonical);
    onChange?.(next.canonical);
  };

  return (
    <div className="relative">
      {/* What is submitted: no separators, no symbol, nothing to interpret. */}
      {name ? <input type="hidden" name={name} value={controlled ? formatAmountInput(value, code).canonical : canonical} /> : null}

      {symbol ? (
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-ink-400">
          {info.symbol}
        </span>
      ) : null}

      <input
        {...rest}
        ref={inputRef}
        type="text"
        placeholder={hint}
        inputMode={info.decimals === 0 ? "numeric" : "decimal"}
        autoComplete="off"
        value={shown}
        onChange={handle}
        className={`field ${symbol ? "pl-9" : ""} ${className}`}
      />
    </div>
  );
}
